import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import api from "../../api/axios";
import { toast } from "react-toastify";

const CheckoutForm = ({ secretKey, orderId, order }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");

  useEffect(() => setClientSecret(secretKey), [secretKey]);

  // Only show status; do NOT mark paid here.
  useEffect(() => {
    if (!stripe || !clientSecret) return;
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!paymentIntent) return;
      const s = paymentIntent.status;
      if (s === "succeeded") setMessage("Payment succeeded.");
      else if (s === "processing") setMessage("Your payment is processing.");
      else if (s === "requires_payment_method") setMessage("Your payment was not successful.");
      else setMessage("Something went wrong.");
    });
  }, [stripe, clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);

    // Prefer 'if_required' so we can handle instant success without redirect
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `https://3dspot.io/dashboard/thankyou/${order._id}`,
      },
      redirect: "if_required",
    });

    if (error) {
      setMessage(error.message || "An unexpected error");
      setIsLoading(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Optional: notify your API (server must verify with Stripe anyway)
      try {
        await api.post("/api/v1/orders/payment", {
          orderId: order._id,
          paymentId: paymentIntent.id,
        });
      } catch (err) {
        // don't block UX on this; webhook should still mark paid
        console.warn(err);
      }
      // Navigate with Stripe params so ThankYou can hard-gate
      navigate(
        `/dashboard/thankyou/${order._id}?payment_intent=${paymentIntent.id}&redirect_status=succeeded`
      );
    } else {
      // If next_action needed (3DS), Stripe will redirect to return_url itself.
      setMessage("Confirming payment...");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!elements) return;
    const paymentElement = elements.create("payment", {
      paymentMethodOrder: ["apple_pay", "google_pay", "card", "klarna"],
      layout: { type: "tabs", defaultCollapsed: false },
      business: { name: "3D Spot" },
      wallets: { applePay: "auto", googlePay: "auto" },
    });
    paymentElement.mount("#payment-element");
  }, [elements]);

  return (
    <form onSubmit={handleSubmit} id="payment-form">
      <div id="payment-element"></div>
      <button className="secondary-button w-full md:w-64 my-4" type="submit" disabled={isLoading || !stripe || !elements}>
        {isLoading ? "Loading..." : "Pay Now"}
      </button>
      {message && <p className="text-sm opacity-80">{message}</p>}
    </form>
  );
};

export default CheckoutForm;
